<script setup>
import Pagination from '@/Components/Pagination/Pagination.vue';
import Table from '@/Components/Table/Table.vue';
import StaffLayout from '@/Layouts/StaffLayout.vue';
import { useForm } from '@inertiajs/vue3';
import { useDebounceFn } from '@vueuse/core';
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = inject('route');
const props = defineProps({
    query: Object,
    reviews: Object,

    flexerOptions: Object,
    positionOptions: Object,
});

const form = useForm({
    search: props.query.search ?? '',
    position: props.query.freelancpositioner ?? '',
    flexer: props.query.flexer ?? '',
    orderBy: props.query.orderBy ?? 'date',
    orderDir: props.query.orderDir ?? 'desc',
    page: props.query.page ?? 1,
});

const getData = (resetPage = true) => {
    form.page = resetPage ? 1 : form.page;

    form.get(
        route('staff.locations.received-reviews.index', {
            location: props.query.location.id,
        }),
        {
            preserveState: true,
            preserveScroll: true,
            only: ['reviews', 'query'],
        }
    );
};

const debouncedGetData = useDebounceFn(() => getData(), 300);

const tableSettings = [
    {
        label: t('Date {model}', { model: 'shift' }),
        orderBy: 'start',
        width: '15%',
    },
    {
        label: t('Details {model}', { model: 'shift' }),
        from: 'lg',
        width: '20%',
    },
    { label: t('Position'), from: 'md', orderBy: 'position', width: '20%' },
    {
        label: t('Fl@xr'),
        from: 'md',
        orderBy: 'flexer',
        width: '20%',
    },
    {
        label: t('Rating'),
        orderBy: 'rating',
        width: '10%',
        component: 'Rating',
    },
    { label: '', width: '5%' },
    { label: '', width: '5%' },
];

const tableData = computed(() =>
    props.reviews.data.map((review) => {
        return {
            id: review.id, // used as vue key if set
            route: route('staff.reviews.show', review.id),
            // colorCode: invoice.color_code, // used to set row color accent
            rowData: [
                review.date,
                review.detail,
                review.position,
                review.flexer_name,
                review.rating,
                review.completed_shift_count,
            ],
        };
    })
);

const setPage = (e) => {
    form.page = e;
    getData(false);
};

onMounted(() => {
    //   getData(false);
});
</script>

<template>
    <StaffLayout :title="$t('Received {model}', { model: $t('reviews') })">
        <div class="flex-between">
            <div class="flex items-center gap-4">
                <FormKit
                    type="search"
                    :placeholder="$t('Search')"
                    name="search"
                    prefix-icon="search"
                    v-model="form.search"
                    :value="form.search"
                    @input="debouncedGetData"
                />

                <FormKit
                    type="select"
                    name="flexer"
                    :options="{
                        '': $t('All {attribute}', { attribute: $t(`Zorgblik's`) }),
                        ...flexerOptions,
                    }"
                    select-icon="down"
                    v-model="form.flexer"
                    :value="form.flexer"
                    @input="getData"
                />
            </div>
            <div class="flex items-center gap-4">
                <FormKit
                    type="select"
                    name="per_page"
                    :options="{
                        ...paginationLengthOptions,
                    }"
                    v-model="form.per_page"
                    select-icon="down"
                    :value="form.per_page"
                    @input="
                        getData();
                        setPerPage();
                    "
                />
            </div>
        </div>

        <template v-if="typeof reviews !== 'undefined' && reviews.data.length > 0">
            <Table
                class="mb-8"
                :loading="form.processing"
                :tableSettings="tableSettings"
                :data="tableData"
                v-model:orderBy="form.orderBy"
                @update:orderBy="getData()"
                v-model:orderDir="form.orderDir"
                @update:orderDir="getData()"
            />

            <pagination
                v-if="typeof reviews !== 'undefined'"
                :links="reviews.links"
                @setPage="setPage"
                :hideTotal="query.search != '' && query.search != null"
            />
        </template>
        <template v-else>
            <div class="w-full px-4 py-3 overflow-hidden font-bold leading-tight bg-white rounded">
                {{ $t('No {model} found', { model: $t('reviews') }) }}
            </div>
        </template>
    </StaffLayout>
</template>
